import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Phone, PhoneIncoming, PhoneOutgoing, Users, X } from 'lucide-react';

const CALL_TYPES = ['Incoming', 'Outgoing', 'Meeting'];
const TAGS = ['Governance', 'Advice', 'Banking', 'Tax', 'Compliance'];

export default function CallLogModal({ 
  isOpen, 
  onClose, 
  onSave, 
  editingCallLog = null,
  individuals = [],
  advisers = []
}) {
  const [formData, setFormData] = useState({
    dateTime: '',
    callType: 'Outgoing',
    participants: [],
    summary: '',
    actions: '',
    tags: [],
    linkedIndividualId: null,
    linkedAdviserId: null,
    linkedEntityId: null
  });

  useEffect(() => {
    if (editingCallLog) {
      // Convert ISO datetime to local datetime-local format
      const dt = new Date(editingCallLog.dateTime);
      const localDateTime = dt.toISOString().slice(0, 16);
      
      setFormData({
        dateTime: localDateTime,
        callType: editingCallLog.callType || 'Outgoing',
        participants: editingCallLog.participants || [],
        summary: editingCallLog.summary || '',
        actions: editingCallLog.actions || '',
        tags: editingCallLog.tags || [],
        linkedIndividualId: editingCallLog.linkedIndividualId,
        linkedAdviserId: editingCallLog.linkedAdviserId,
        linkedEntityId: editingCallLog.linkedEntityId
      });
    } else {
      // Default to current date/time
      const now = new Date();
      const localDateTime = now.toISOString().slice(0, 16);
      
      setFormData({
        dateTime: localDateTime,
        callType: 'Outgoing',
        participants: [],
        summary: '',
        actions: '',
        tags: [],
        linkedIndividualId: null,
        linkedAdviserId: null,
        linkedEntityId: null
      });
    }
  }, [editingCallLog, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Convert local datetime to ISO string
    const dt = new Date(formData.dateTime);
    const isoDateTime = dt.toISOString();
    
    onSave({
      ...formData,
      dateTime: isoDateTime
    });
    onClose();
  };

  const addParticipant = (id, name, type) => {
    if (formData.participants.some(p => p.id === id)) return;
    setFormData({
      ...formData,
      participants: [...formData.participants, { id, name, type }]
    });
  };

  const removeParticipant = (id) => {
    setFormData({
      ...formData,
      participants: formData.participants.filter(p => p.id !== id)
    });
  };

  const toggleTag = (tag) => {
    if (formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
    } else {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
    }
  };

  const getCallTypeIcon = (type) => {
    switch (type) {
      case 'Incoming': return <PhoneIncoming className="w-4 h-4" />;
      case 'Outgoing': return <PhoneOutgoing className="w-4 h-4" />;
      case 'Meeting': return <Users className="w-4 h-4" />;
      default: return <Phone className="w-4 h-4" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingCallLog ? 'Edit Call Log' : 'Log New Call'}</DialogTitle>
          <DialogDescription>
            Record conversation details and key outcomes
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          {/* Date/Time and Call Type Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="dateTime">Date & Time *</Label>
              <Input
                id="dateTime"
                type="datetime-local"
                value={formData.dateTime}
                onChange={(e) => setFormData({ ...formData, dateTime: e.target.value })}
                className="caffeine-input"
                required
                data-testid="call-log-datetime"
              />
            </div>
            <div>
              <Label htmlFor="callType">Call Type *</Label>
              <Select 
                value={formData.callType} 
                onValueChange={(value) => setFormData({ ...formData, callType: value })}
              >
                <SelectTrigger data-testid="call-log-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CALL_TYPES.map(type => (
                    <SelectItem key={type} value={type}>
                      <span className="flex items-center gap-2">
                        {getCallTypeIcon(type)}
                        {type}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Participants */}
          <div>
            <Label>Participants</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.participants.map(p => (
                <Badge 
                  key={p.id} 
                  variant="secondary"
                  className="flex items-center gap-1 bg-[#4A3B32]/10 text-[#4A3B32]"
                >
                  {p.name}
                  <button
                    type="button"
                    onClick={() => removeParticipant(p.id)}
                    className="ml-1 hover:text-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Select onValueChange={(value) => {
                const [type, id] = value.split(':');
                const person = type === 'individual' 
                  ? individuals.find(i => i.id === id)
                  : advisers.find(a => a.id === id);
                if (person) {
                  const name = type === 'individual' 
                    ? `${person.firstName} ${person.lastName}`
                    : person.name;
                  addParticipant(id, name, type);
                }
              }}>
                <SelectTrigger data-testid="add-participant">
                  <SelectValue placeholder="Add participant..." />
                </SelectTrigger>
                <SelectContent>
                  {individuals.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs text-[#8C8078] font-medium">Individuals</div>
                      {individuals.map(ind => (
                        <SelectItem key={ind.id} value={`individual:${ind.id}`}>
                          {ind.firstName} {ind.lastName}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {advisers.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs text-[#8C8078] font-medium mt-2">Advisers</div>
                      {advisers.map(adv => (
                        <SelectItem key={adv.id} value={`adviser:${adv.id}`}>
                          {adv.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Summary */}
          <div>
            <Label htmlFor="summary">Summary *</Label>
            <Textarea
              id="summary"
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              placeholder="Key points discussed..."
              className="caffeine-input min-h-[100px]"
              required
              data-testid="call-log-summary"
            />
          </div>

          {/* Actions/Next Steps */}
          <div>
            <Label htmlFor="actions">Actions / Next Steps</Label>
            <Textarea
              id="actions"
              value={formData.actions}
              onChange={(e) => setFormData({ ...formData, actions: e.target.value })}
              placeholder="Follow-up items..."
              className="caffeine-input min-h-[80px]"
              data-testid="call-log-actions"
            />
          </div>

          {/* Tags */}
          <div>
            <Label>Tags (optional)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {TAGS.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    formData.tags.includes(tag)
                      ? 'bg-[#4A3B32] text-white'
                      : 'bg-[#F0EBE6] text-[#5D524D] hover:bg-[#E6E0D9]'
                  }`}
                  data-testid={`tag-${tag.toLowerCase()}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t border-[#E6E0D9]">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#4A3B32] hover:bg-[#3A2B22]"
              data-testid="save-call-log-btn"
            >
              {editingCallLog ? 'Update' : 'Save'} Call Log
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
